/**
 * Multi-unit access helpers, mirroring functions/src/units.ts (pure).
 *
 * Every scoped document carries `unitIds` (its company, administration and
 * department ids). A user may have several placements (profile.placements,
 * the primary one first); a visit or a new customer is stamped with one of
 * them (the "posting as" placement).
 */
import type { OrgPlacement, UserProfile } from "./types";

/** The non-null ids of one placement, company first. */
export function placementUnitIds(p: OrgPlacement): string[] {
  return [p.companyId, p.administrationId, p.departmentId].filter((x): x is string => !!x);
}

export function samePlacement(a: OrgPlacement, b: OrgPlacement): boolean {
  return a.companyId === b.companyId && a.administrationId === b.administrationId && a.departmentId === b.departmentId;
}

/** The profile's primary placement. */
export function primaryPlacement(profile: Pick<UserProfile, "companyId" | "administrationId" | "departmentId"> | null | undefined): OrgPlacement {
  return {
    companyId: profile?.companyId ?? null,
    administrationId: profile?.administrationId ?? null,
    departmentId: profile?.departmentId ?? null,
  };
}

/** Every placement of a profile, the primary one first (profiles from before placements: just that one). */
export function placementsOf(profile: Pick<UserProfile, "companyId" | "administrationId" | "departmentId" | "placements"> | null | undefined): OrgPlacement[] {
  const primary = primaryPlacement(profile);
  const out: OrgPlacement[] = [primary];
  for (const p of profile?.placements ?? []) {
    const q = { companyId: p.companyId ?? null, administrationId: p.administrationId ?? null, departmentId: p.departmentId ?? null };
    if (placementUnitIds(q).length && !out.some((x) => samePlacement(x, q))) out.push(q);
  }
  return out;
}

/** The extra placements (all but the primary one). */
export function extraPlacementsOf(profile: Parameters<typeof placementsOf>[0]): OrgPlacement[] {
  return placementsOf(profile).slice(1);
}

/** The profile's unit ids (stored, else derived from its placements). */
export function unitIdsOfProfile(profile: Parameters<typeof placementsOf>[0] & { unitIds?: string[] } | null | undefined): string[] {
  if (profile?.unitIds) return profile.unitIds;
  const out: string[] = [];
  for (const p of placementsOf(profile)) for (const id of placementUnitIds(p)) if (!out.includes(id)) out.push(id);
  return out;
}

/** A user doc's units (stored, else its single placement's). */
export function userUnitIds(user: { unitIds?: string[] } & OrgPlacement): string[] {
  return user.unitIds ?? placementUnitIds(user);
}

/** True when the two lists share an id. */
export function sharesUnit(a: readonly string[] | null | undefined, b: readonly string[] | null | undefined): boolean {
  if (!a?.length || !b?.length) return false;
  const set = new Set(a);
  return b.some((id) => set.has(id));
}

/**
 * The org units a unit scope covers (a General Manager's subtree, plus "Can
 * see" units): every unit in or under the scope units, and the units above
 * them only as the path to them (a department manager's administration and
 * company, so the tree can be drawn). Sibling branches are left out: an
 * administration GM gets their company, their administration and its
 * departments, never the company's other administrations.
 */
export function unitsInScope<
  C extends { id: string },
  A extends { id: string; companyId: string },
  D extends { id: string; administrationId: string; companyId: string },
>(scopeIds: readonly string[], units: { companies: readonly C[]; administrations: readonly A[]; departments: readonly D[] }): { companies: C[]; administrations: A[]; departments: D[] } {
  const ids = new Set(scopeIds);
  const departments = units.departments.filter((u) => ids.has(u.id) || ids.has(u.administrationId) || ids.has(u.companyId));
  const administrations = units.administrations.filter(
    (u) => ids.has(u.id) || ids.has(u.companyId) || departments.some((d) => d.administrationId === u.id),
  );
  const companies = units.companies.filter((u) => ids.has(u.id) || administrations.some((a) => a.companyId === u.id));
  return { companies, administrations, departments };
}
